import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { copy as sharedCopy } from '@shared/copy';
import { Dialog } from '@shared/ui/dialog';

import {
  ROOT_PARENT_VALUE,
  termFormSchema,
  toParentId,
  type TermFormMessages,
  type TermFormValues,
} from './forms.schema';

/**
 * The one dialog both vocabulary surfaces open, in three modes — edit, add a
 * root, and add a child under a node.
 *
 * **The tag page gets the same dialog with one field fewer.** `parent` is
 * `null` there and the control is simply not rendered; the component reads that
 * absence rather than branching on which page it stands on. A second dialog and
 * a second initializer would agree today and drift at the first edit — which is
 * the reason the UI/UX pack gives for building it this way, and it holds harder
 * in code than in markup.
 *
 * **The parent field lists full paths.** «دراما › تاريخي» and never «تاريخي»,
 * because two children under different parents may share a name and a bare name
 * cannot tell them apart. The node being edited and every descendant of it are
 * removed from the list: re-parenting a branch under one of its own children is
 * a cycle, and the tree would stop being a tree.
 *
 * `<dialog>` opened with `showModal()` carries the focus trap, the backdrop,
 * `Esc`, and focus return to the trigger — all four are the browser's, none is
 * hand-written.
 *
 * **The cancel button is a `type="button"`**, so it never runs constraint
 * validation and can always do the one thing it exists for; it carries
 * `formNoValidate` as well, because the repo's static gate greps for it on every
 * cancel inside a dialog and a reader should not have to know why one is exempt.
 */

export interface ParentFieldSpec {
  label: string;
  hint: string;
  rootOptionLabel: string;
  /** Full paths, depth-first, already excluding the node and its descendants. */
  options: readonly { value: string; label: string }[];
}

export function TermDialog({
  open,
  title,
  context,
  nameLabel,
  nameHint,
  saveLabel,
  messages,
  defaultName,
  defaultParentId,
  parent,
  busy = false,
  onSubmit,
  onClose,
}: {
  open: boolean;
  /** Composed by the surface from the mode — «حرّر التصنيف» · «أضف تصنيفاً فرعياً». */
  title: string;
  /** The section the record belongs to, under the heading. */
  context: string;
  nameLabel: string;
  nameHint?: string;
  saveLabel: string;
  messages: TermFormMessages;
  defaultName: string;
  defaultParentId: string;
  /** `null` on the tag surface — the field is absent, never disabled. */
  parent: ParentFieldSpec | null;
  busy?: boolean;
  onSubmit: (values: { name: string; parentId: string | null }) => void;
  onClose: () => void;
}) {
  const headingId = useId();
  const contextId = useId();
  const nameId = useId();
  const nameHintId = useId();
  const nameErrorId = useId();
  const parentId = useId();
  const parentHintId = useId();
  const nameRef = useRef<HTMLInputElement>(null);

  const schema = useMemo(() => termFormSchema(messages), [messages]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TermFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: defaultName, parentId: defaultParentId },
  });

  // The dialog is one element serving three modes, so it is re-seeded on every
  // open rather than remounted: a stale name left in the field from the previous
  // row is the defect a `key` would paper over and a reset actually removes.
  useEffect(() => {
    if (!open) return;
    reset({ name: defaultName, parentId: defaultParentId });
    // The browser focuses the first tabbable element on `showModal()`, which is
    // the field; this makes that true even when a mode change re-renders after.
    window.requestAnimationFrame(() => nameRef.current?.select());
  }, [open, defaultName, defaultParentId, reset]);

  const nameRegistration = register('name');

  return (
    <Dialog open={open} labelledBy={headingId} onClose={onClose}>
      <form
        noValidate
        onSubmit={handleSubmit((values) =>
          onSubmit({ name: values.name.trim(), parentId: toParentId(values.parentId) }),
        )}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="mb-2 font-display text-h3">
            {title}
          </h2>
          <p id={contextId} className="mb-0 max-w-none text-xs text-ink-muted">
            {context}
          </p>
        </div>

        <div>
          <label htmlFor={nameId} className="mb-2 block text-sm font-medium">
            {nameLabel}
          </label>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            aria-required="true"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={
              [errors.name ? nameErrorId : null, nameHint ? nameHintId : null].filter(Boolean).join(' ') ||
              undefined
            }
            className="hit-target w-full rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-body text-ink placeholder:text-ink-faint hover:border-ink-muted focus-visible:border-accent"
            {...nameRegistration}
            ref={(element) => {
              nameRegistration.ref(element);
              nameRef.current = element;
            }}
          />
          {errors.name ? (
            <p id={nameErrorId} className="mb-0 mt-2 max-w-none text-xs text-danger">
              {errors.name.message}
            </p>
          ) : null}
          {nameHint ? (
            <p id={nameHintId} className="mb-0 mt-2 max-w-none text-xs text-ink-muted">
              {nameHint}
            </p>
          ) : null}
        </div>

        {parent ? (
          <div>
            <label htmlFor={parentId} className="mb-2 block text-sm font-medium">
              {parent.label}
            </label>
            <select
              id={parentId}
              aria-describedby={parentHintId}
              className="hit-target w-full cursor-pointer rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-body text-ink hover:border-ink-muted focus-visible:border-accent"
              {...register('parentId')}
            >
              <option value={ROOT_PARENT_VALUE}>{parent.rootOptionLabel}</option>
              {parent.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p id={parentHintId} className="mb-0 mt-2 max-w-none text-xs text-ink-muted">
              {parent.hint}
            </p>
          </div>
        ) : null}

        {/*
          «احفظ» first from the reading-start edge, then «ألغِ» as a ghost button
          rather than a link — the pack's §9.2, and the inverse of the
          destructive dialog's order, where the safe choice leads.
        */}
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={busy}
            className="rounded-sm bg-accent px-5 py-3 text-sm font-semibold text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {saveLabel}
          </button>
          <button
            type="button"
            formNoValidate
            onClick={onClose}
            className="rounded-sm px-5 py-3 text-sm font-semibold text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
